import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ShieldCheck, HardHat, AlertTriangle, FileCheck2, PenLine, FolderOpen } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { Card, Pill, Spinner, type Tone } from "@/components/ui";
import SiteMapsCard from "@/features/sitemaps/SiteMapsCard";
import { hasRole, useRequireAuth } from "@/lib/auth";

// =============================================================================
// Project HSE folder — T-430 (fills the T-016 "site folder" stub)
// =============================================================================
// One place per project: its start cards, permits, incidents and site
// emergency profile. Everything already carries project_id — this is the
// assembly view James asked for ("see all the project's HSE docs in one
// place instead of nested in HSE").
// =============================================================================

const SEVERITY_TONE: Record<string, Tone> = {
  report_only: "default", // mig 411
  property_damage: "notice", // mig 482
  environmental: "notice", // mig 485
  near_miss: "notice", first_aid: "pending", medical_treatment: "warning",
  lost_time: "warning", serious: "critical", notifiable: "critical", fatality: "critical",
};

function fmtDate(d?: string | null) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" });
}

export function ProjectHseTab({ project }: { project: any }) {
  const projectId = project.project_id as string;
  const user = useRequireAuth();
  const canDraw = hasRole(user, "field");   // mig 391 — the crew draws; a manager locks
  const today = new Date().toISOString().slice(0, 10);

  const { data: cards = [], isLoading: l1 } = useQuery({
    queryKey: ["proj_hse_cards", projectId],
    queryFn: async () => {
      const { data } = await supabase.from("hse_start_cards")
        .select("card_id, card_number, card_date, status, work_area, risk_answers, submitted_at")
        .eq("project_id", projectId)
        .order("card_date", { ascending: false }).limit(30);
      return (data ?? []) as any[];
    },
  });

  const { data: cardSignons = new Map() } = useQuery({
    queryKey: ["proj_hse_card_signons", cards.map(c => c.card_id).join(",")],
    enabled: cards.length > 0,
    queryFn: async () => {
      const { data } = await supabase.from("hse_start_card_signons")
        .select("card_id").in("card_id", cards.map(c => c.card_id));
      const m = new Map<string, number>();
      for (const r of data ?? []) m.set((r as any).card_id, (m.get((r as any).card_id) ?? 0) + 1);
      return m;
    },
  });

  const { data: permits = [], isLoading: l2 } = useQuery({
    queryKey: ["proj_hse_permits", projectId],
    queryFn: async () => {
      const { data } = await supabase.from("permits")
        .select("permit_id, permit_number, permit_type, status, valid_from, valid_until")
        .eq("project_id", projectId)
        .order("issued_at", { ascending: false }).limit(30);
      return (data ?? []) as any[];
    },
  });

  // T-MOP — generated Methods of Procedure live with the rest of the
  // project's HSE record (James, 10 Sep: MOPs surface on the HSE tab
  // beside SWMS and lift plans).
  const { data: mops = [] } = useQuery({
    queryKey: ["proj_hse_mops", projectId],
    queryFn: async () => {
      const { data } = await (supabase as any).from("mops")
        .select("mop_id, mop_number, title, status, version, approved_at, created_at")
        .eq("project_id", projectId).neq("status", "superseded")
        .order("created_at", { ascending: false }).limit(20);
      return (data ?? []) as any[];
    },
  });

  const { data: incidents = [], isLoading: l3 } = useQuery({
    queryKey: ["proj_hse_incidents", projectId],
    queryFn: async () => {
      const { data } = await supabase.from("incidents")
        .select("incident_id, incident_number, severity, incident_type, status, occurred_at, short_description, notifiable_to_regulator")
        .eq("project_id", projectId)
        .order("occurred_at", { ascending: false }).limit(20);
      return (data ?? []) as any[];
    },
  });

  const isLoading = l1 || l2 || l3;
  const todayCard = cards.find(c => c.card_date === today);
  const openIncidents = incidents.filter(i => !["closed", "rejected_duplicate"].includes(i.status));
  const activePermits = permits.filter(p => ["issued", "active"].includes(p.status));
  const isUnderway = ["live", "mobilising", "acceptance_pending"].includes(project.status);

  return (
    <div className="space-y-4">
      {/* Today banner — the pulse check */}
      {isUnderway && (
        <div className={`rounded-md border px-4 py-3 flex items-center gap-3 flex-wrap ${
          todayCard?.status === "submitted" ? "border-ok/30 bg-ok/8"
          : todayCard ? "border-warn/40 bg-warn/10"
          : "border-danger/40 bg-danger/10"}`}>
          {todayCard?.status === "submitted" ? (
            <>
              <ShieldCheck size={16} className="text-ok"/>
              <span className="text-[13px] text-ok">Today's start card submitted — <span className="font-mono">{todayCard.card_number}</span>, {(cardSignons as Map<string, number>).get(todayCard.card_id) ?? 0} signed on.</span>
            </>
          ) : todayCard ? (
            <>
              <AlertTriangle size={16} className="text-warn"/>
              <span className="text-[13px] text-warn">Today's card is still a draft — <span className="font-mono">{todayCard.card_number}</span>.</span>
            </>
          ) : (
            <>
              <AlertTriangle size={16} className="text-danger"/>
              <span className="text-[13px] text-danger">No start card today. If a crew is on site, that's the first job.</span>
            </>
          )}
          <Link to={todayCard ? `/hse/start-card/${todayCard.card_id}` : "/hse/start-card"} className="text-[12px] text-accent hover:underline ml-auto">
            {todayCard ? "Open card" : "Start cards"}
          </Link>
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MiniKpi icon={HardHat} label="Start cards" value={cards.length}/>
        <MiniKpi icon={FileCheck2} label="Active permits" value={activePermits.length}/>
        <MiniKpi icon={AlertTriangle} label="Open incidents" value={openIncidents.length} accent={openIncidents.length > 0}/>
        <MiniKpi icon={PenLine} label="Sign-ons (30 cards)" value={[...(cardSignons as Map<string, number>).values()].reduce((a, b) => a + b, 0)}/>
      </div>

      {isLoading && <div className="flex justify-center py-8"><Spinner size={18}/></div>}

      {/* Emergency profile */}
      <Card>
        <div className="p-4">
          <div className="text-[11px] uppercase tracking-wider text-text-muted mb-2">Site emergency profile</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-[13px]">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-text-muted">Nearest hospital</div>
              <div className="text-text">{project.sites?.nearest_hospital ?? "— set it on the next start card (save-to-site)"}</div>
              {project.sites?.nearest_hospital_phone && <div className="text-text-muted font-mono text-[12px]">{project.sites.nearest_hospital_phone}</div>}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-text-muted">Evacuation point</div>
              <div className="text-text">{project.sites?.evacuation_point ?? "—"}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-text-muted">HSE profile</div>
              <div className="text-text">
                {project.is_high_risk_construction_work ? "HRCW" : "Non-HRCW"}
                {project.requires_jmp ? " · JMP required" : ""}
                {project.risk_rating ? ` · ${project.risk_rating}` : ""}
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Start cards */}
      <Card>
        <div className="p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] uppercase tracking-wider text-text-muted">Start cards</div>
            <Link to="/hse/start-card" className="text-[12px] text-accent hover:underline">All start cards</Link>
          </div>
          {cards.length === 0 && <div className="text-[12px] text-text-muted italic">None yet for this project.</div>}
          <div className="divide-y divide-line/40">
            {cards.map(c => {
              const warnings = (c.risk_answers?.submit_warnings ?? []) as string[];
              return (
                <Link key={c.card_id} to={`/hse/start-card/${c.card_id}`}
                  className="flex items-center gap-3 py-2 flex-wrap hover:bg-ink-raised/40 -mx-2 px-2 rounded transition">
                  <span className="font-mono text-[12px] text-accent w-[90px]">{c.card_number}</span>
                  <span className="text-[12px] text-text-muted w-[84px]">{fmtDate(c.card_date)}</span>
                  <span className="text-[12px] text-text flex-1 min-w-[140px] truncate">{c.work_area ?? "—"}</span>
                  <span className="text-[12px] text-text-muted inline-flex items-center gap-1"><PenLine size={11}/> {(cardSignons as Map<string, number>).get(c.card_id) ?? 0}</span>
                  {warnings.length > 0 && <Pill label={`${warnings.length} gap${warnings.length > 1 ? "s" : ""} at submit`} tone="warning" small/>}
                  <Pill label={c.status} tone={c.status === "submitted" ? "ok" : "pending"} small/>
                </Link>
              );
            })}
          </div>
        </div>
      </Card>

      {/* Whose paperwork + the folder setup moved to the project's Site folder
          tab (mig 393, 27 Sep 2026). */}
      <div className="rounded-md border border-line px-4 py-2.5 text-[12px] text-text-muted flex items-center gap-2 flex-wrap">
        <FolderOpen size={13} className="text-accent"/>
        <span>Whose paperwork runs this job, the scope rules, SWMS allocation, permits and the manager sign-off live on the</span>
        <Link to={`/projects/${projectId}?tab=folder`} className="text-accent hover:underline font-medium">Site folder tab</Link>
        <span>· {project.hse_system === "coretel" ? "Coretel paperwork" : `client paperwork${project.hse_client_platform ? ` (${project.hse_client_platform})` : ""}`}</span>
      </div>

      {/* Site maps (mig 390, 26 Sep 2026) — the mud map lives in the project's
          site folder; maps drawn on the BID it came from show here too. */}
      <SiteMapsCard projectId={projectId} sourceBidId={project.source_bid_id ?? null} canEdit={canDraw}/>

      {/* Generated MOPs */}
      <Card>
        <div className="p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] uppercase tracking-wider text-text-muted">Methods of Procedure</div>
            <Link to="/hse/mops" className="text-[12px] text-accent hover:underline">MOP builder</Link>
          </div>
          {mops.length === 0 && <div className="text-[12px] text-text-muted italic">None yet for this project.</div>}
          <div className="divide-y divide-line/40">
            {mops.map(m => (
              <Link key={m.mop_id} to={`/hse/mops/${m.mop_id}`}
                className="flex items-center gap-3 py-2 flex-wrap hover:bg-ink-raised/40 -mx-2 px-2 rounded transition">
                <span className="font-mono text-[12px] text-accent w-[104px]">{m.mop_number}</span>
                <span className="text-[13px] text-text flex-1 min-w-[160px] truncate">{m.title}</span>
                <span className="text-[12px] text-text-muted">{fmtDate(m.created_at)}</span>
                {m.status === "approved"
                  ? <Pill label={`approved v${m.version}`} tone="ok" small/>
                  : <Pill label="draft — not for use" tone="warning" small/>}
              </Link>
            ))}
          </div>
        </div>
      </Card>

      {/* Permits */}
      <Card>
        <div className="p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] uppercase tracking-wider text-text-muted">Permits</div>
            <Link to="/permits" className="text-[12px] text-accent hover:underline">Permit register</Link>
          </div>
          {permits.length === 0 && <div className="text-[12px] text-text-muted italic">No permits on this project.</div>}
          <div className="divide-y divide-line/40">
            {permits.map(p => (
              <Link key={p.permit_id} to={`/permits/${p.permit_id}`}
                className="flex items-center gap-3 py-2 flex-wrap hover:bg-ink-raised/40 -mx-2 px-2 rounded transition">
                <span className="font-mono text-[12px] text-accent w-[110px]">{p.permit_number}</span>
                <span className="text-[13px] text-text flex-1 capitalize min-w-[140px]">{String(p.permit_type).replace(/_/g, " ")}</span>
                <span className="text-[12px] text-text-muted">{fmtDate(p.valid_from)} → {fmtDate(p.valid_until)}</span>
                <Pill label={p.status} tone={p.status === "active" ? "ok" : p.status === "issued" ? "pending" : "closed"} small/>
              </Link>
            ))}
          </div>
        </div>
      </Card>

      {/* Incidents */}
      <Card>
        <div className="p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] uppercase tracking-wider text-text-muted">Incidents</div>
            <Link to="/hse/incidents" className="text-[12px] text-accent hover:underline">Incident register</Link>
          </div>
          {incidents.length === 0 && <div className="text-[12px] text-text-muted italic">No incidents recorded — keep it that way.</div>}
          <div className="divide-y divide-line/40">
            {incidents.map(i => (
              <Link key={i.incident_id} to="/hse/incidents"
                className="flex items-center gap-3 py-2 flex-wrap hover:bg-ink-raised/40 -mx-2 px-2 rounded transition">
                <span className="font-mono text-[12px] text-accent w-[90px]">{i.incident_number}</span>
                <Pill label={String(i.severity).replace(/_/g, " ")} tone={SEVERITY_TONE[i.severity] ?? "default"} small/>
                <span className="text-[13px] text-text flex-1 min-w-[160px] truncate">{i.short_description}</span>
                {i.notifiable_to_regulator && <Pill label="notifiable" tone="critical" small/>}
                <span className="text-[12px] text-text-muted">{fmtDate(i.occurred_at)}</span>
                <Pill label={String(i.status).replace(/_/g, " ")} tone={["closed", "rejected_duplicate"].includes(i.status) ? "closed" : "pending"} small/>
              </Link>
            ))}
          </div>
        </div>
      </Card>
    </div>
  );
}

function MiniKpi({ icon: Icon, label, value, accent = false }: { icon: any; label: string; value: number; accent?: boolean }) {
  return (
    <Card>
      <div className="p-3 flex items-center gap-2.5">
        <Icon size={15} className={accent ? "text-danger" : "text-accent"}/>
        <div>
          <div className={`text-[18px] font-semibold leading-none ${accent ? "text-danger" : "text-text"}`}>{value}</div>
          <div className="text-[10px] uppercase tracking-wider text-text-muted mt-0.5">{label}</div>
        </div>
      </div>
    </Card>
  );
}
